'use client';

import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipContentProps,
  TooltipProvider,
  TooltipTrigger,
} from '#components/atoms/tooltip';
import { useTrackOverflow } from '#hooks';
import { cn } from '#lib/utils';
import { ComponentProps, ReactNode, useRef } from 'react';

type ChipProps = {
  selected?: boolean;
  children?: ReactNode;
  size?: 'large' | 'small' | 'medium';
  contentProps?: TooltipContentProps;
} & Pick<ComponentProps<'button'>, 'onClick'>;

export const Chip = ({
  selected,
  children,
  size = 'medium',
  contentProps = {},
  ...props
}: ChipProps) => {
  const measureRef = useRef<HTMLButtonElement>(null);
  const { isOverflowing, initialized } = useTrackOverflow(measureRef);

  const chipComponent = (
    <button
      {...props}
      style={{
        maxWidth: '25rem',
      }}
      ref={measureRef}
      type="button"
      className={cn(
        `text-nowrap overflow-x-hidden text-ellipsis tds-chip tds-chip--selection tds-chip--selection-${size}`,
        {
          'tds-chip--selected': selected,
          '!block': true,
        },
      )}
    >
      <span className="tds-chip--text">{children}</span>
    </button>
  );

  if (initialized && isOverflowing) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger disabled={!isOverflowing} asChild>
            {chipComponent}
          </TooltipTrigger>
          {isOverflowing && (
            <TooltipContent className="max-w-[25rem]" {...contentProps}>
              {children}
              <TooltipArrow className="" />
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    );
  }

  return chipComponent;
};
